// Profile tab editing (B6) — single source for the carried intake
// sections: display AND the per-section edit drawer render from this
// config so labels, field keys (camelCase — must match the server's
// editedFields provenance keys) and option lists can't drift.

import type { LoanIntakeProfileDto } from "@/types/intakeProfile";

export type IntakeSectionId =
  | "structure"
  | "seller"
  | "buyer"
  | "transaction"
  | "motivation"
  | "broker";

export type IntakeFieldType = "money" | "text" | "textarea" | "select" | "yesno";

export interface IntakeFieldConfig {
  /** camelCase property key inside the section object (and the server's
   *  provenance key). */
  key: string;
  label: string;
  type: IntakeFieldType;
  /** Enum member → display label (selects only). */
  options?: { value: string; label: string }[];
  wide?: boolean;
}

export interface IntakeSectionConfig {
  id: IntakeSectionId;
  title: string;
  fields: IntakeFieldConfig[];
}

const humanize = (value: string) => value.replace(/([a-z])([A-Z])/g, "$1 $2");

const opts = (...members: string[]) =>
  members.map((m) => ({ value: m, label: humanize(m) }));

export const INTAKE_SECTIONS: IntakeSectionConfig[] = [
  {
    id: "structure",
    title: "Deal structure",
    fields: [
      { key: "purchasePrice", label: "Purchase price", type: "money" },
      { key: "sellerNoteAmount", label: "Seller note", type: "money" },
      { key: "cashAtClosing", label: "Cash at closing", type: "money" },
      { key: "subordinatedDebtAmount", label: "Subordinated debt", type: "money" },
      { key: "collateralType", label: "Collateral type", type: "text" },
      {
        key: "realEstateIncluded",
        label: "Real estate",
        type: "select",
        options: [
          { value: "None", label: "Not included" },
          { value: "Owned", label: "Included (owned)" },
          { value: "Leased", label: "Included (leased)" },
        ],
      },
    ],
  },
  {
    id: "seller",
    title: "Seller & deal structure",
    fields: [
      { key: "sellerAgeBand", label: "Seller age band", type: "text" },
      { key: "downPaymentAmount", label: "Down payment amount", type: "money" },
      { key: "downPaymentSource", label: "Down payment source", type: "text" },
    ],
  },
  {
    id: "buyer",
    title: "Buyer",
    fields: [
      { key: "buyerBackgroundSummary", label: "Background summary", type: "textarea", wide: true },
      {
        key: "rolePostAcquisition",
        label: "Role post-acquisition",
        type: "select",
        options: opts("OwnerOperator", "InvestorManager", "Other"),
      },
      { key: "financialCapacityVerified", label: "Financial capacity verified", type: "yesno" },
      {
        key: "personalGuaranteeStrength",
        label: "Personal guarantee strength",
        type: "select",
        options: opts("Strong", "Moderate", "Weak", "Unknown"),
      },
    ],
  },
  {
    id: "transaction",
    title: "Transaction status",
    fields: [
      { key: "outstandingConditions", label: "Outstanding conditions", type: "textarea", wide: true },
      { key: "knownIssues", label: "Known issues", type: "textarea", wide: true },
      {
        key: "brokerViewWhySellerFinancing",
        label: "Broker view — why seller financing",
        type: "textarea",
        wide: true,
      },
      { key: "competingSolutions", label: "Competing solutions", type: "textarea", wide: true },
    ],
  },
  {
    id: "motivation",
    title: "Seller motivation & readiness",
    fields: [
      { key: "reasonForSale", label: "Reason for sale", type: "textarea", wide: true },
      { key: "sellerMotivation", label: "Seller motivation", type: "textarea", wide: true },
      { key: "sellerReadiness", label: "Seller readiness", type: "textarea", wide: true },
      {
        key: "sellerRiskTolerance",
        label: "Risk tolerance",
        type: "select",
        options: opts("Low", "Moderate", "High", "Unknown"),
      },
      {
        key: "liquidityTimeline",
        label: "Liquidity timeline",
        type: "select",
        options: opts("Immediate", "ShortTerm", "LongTerm", "Flexible"),
      },
      {
        key: "sellerPrimaryConcern",
        label: "Primary concern",
        type: "select",
        options: opts(
          "MaxProceeds", "BuyerQuality", "ClosingSpeed",
          "EmployeeContinuity", "LegacyProtection", "Other",
        ),
      },
      {
        key: "phoenixSolutionPresented",
        label: "Phoenix solution presented",
        type: "select",
        options: opts("NotYet", "Presented", "Accepted", "Declined"),
      },
      {
        key: "whySellerFinancing",
        label: "Why seller financing (seller)",
        type: "textarea",
        wide: true,
      },
    ],
  },
  {
    id: "broker",
    title: "Broker",
    fields: [
      {
        key: "brokerRepresentation",
        label: "Representation",
        type: "select",
        options: opts("Seller", "Buyer", "Both", "Neither"),
      },
      { key: "brokerFeeArrangement", label: "Fee arrangement", type: "text" },
      { key: "feeAgreementDocumented", label: "Fee agreement documented", type: "yesno" },
      { key: "otherBrokers", label: "Other brokers", type: "text" },
      {
        key: "paymentPreference",
        label: "Payment preference",
        type: "select",
        options: opts("AtClosing", "OnSellerNotePayments", "Hybrid", "Other"),
      },
    ],
  },
];

export type IntakeFieldValue = string | number | boolean | null;

/** A section of the profile viewed as a plain key → value bag (the section
 *  interfaces are flat camelCase scalar maps, so this cast is safe). */
export function sectionValues(
  profile: LoanIntakeProfileDto,
  sectionId: IntakeSectionId,
): Record<string, IntakeFieldValue> {
  return profile[sectionId] as unknown as Record<string, IntakeFieldValue>;
}
